import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addToCart, selectCartItems, selectCartTotal, fetchCart } from '../cart/cartSlice';
import { trackVisit, updateVisitDuration, markConversion } from '../visits/visitSlice';
import visitTracker from '../../utils/visitTracker';
import { getAllImageUrls, getFallbackImageUrl } from '../../utils/imageUtils';
import { getCustomerBaseUrl } from '../../utils/customerBase';
import { isWhatsAppOrdersEnabled } from '../../utils/whatsappOrder';
import WhatsAppOrderModal from '../../components/WhatsAppOrderModal';
import Button from '../../components/ui/Button';
import AppointmentServicePage from '../../components/AppointmentServicePage';
import UnitQuantityInput from '../../components/UnitQuantityInput';
import ProductViewerCount from '../../components/storefront/ProductViewerCount';
import axios from '../../axiosConfig';
import QRCode from 'qrcode';
import { useGA4 } from '../../contexts/GA4Context';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import {
  findStorefrontVariantBySelection,
  getStorefrontSelectedVariantAvailability,
  getStorefrontVariantAvailability,
  getStorefrontVariantIdentity
} from '../../utils/storefrontVariantUtils';
import '../../themes/edition/EditionProduct.css';
import '../../themes/atelier/AtelierProduct.css';
import '../../themes/gallery/GalleryProduct.css';

const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const ProductPage = () => {
  const { productSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Call hook ONCE, unconditionally
  const outletContext = useOutletContext() || {};
  const { store = {}, theme, themeStyles, premiumThemeConfig, onOpenCart } = outletContext;
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';

  const storeSlug = store?.slug;
  const onSubdomain = isOnSubdomain();
  const getStorePath = (path = '') => {
    if (onSubdomain) return path ? `/${path}` : '/';
    return path ? `/s/${storeSlug}/${path}` : `/s/${storeSlug}`;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const customerId = useSelector(state => state.customer?.customer?._id);
  const { trackViewItem, trackAddToCart } = useGA4();
  const hasTrackedProduct = useRef(false);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [mediaReady, setMediaReady] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [unitQuantity, setUnitQuantity] = useState(0.1); // For unit-based products
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [currentDisplayPrice, setCurrentDisplayPrice] = useState(0);
  const [checkoutQR, setCheckoutQR] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Get currency formatting from storefront context
  const { formatPrice, storeCurrency } = useStorefrontCurrency();

  // Currency formatter helper that uses the context
  const currency = {
    format: (amount) => formatPrice(amount)
  };

  useEffect(() => {
    setMediaReady(false);
  }, [selectedImage, product?._id, product?.id]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching product with slug:', productSlug);

      // Check if productSlug looks like an ObjectId (24 hex characters)
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(productSlug);

      let response;
      if (isObjectId) {
        // If it looks like an ObjectId, try ID endpoint first
        console.log('ProductSlug looks like ObjectId, trying ID endpoint first...');
        try {
          response = await axios.get(`/products/${productSlug}`);
          console.log('ID endpoint success:', response.data);
        } catch (err) {
          console.log('ID endpoint failed:', err.response?.status, err.response?.data);
          throw err;
        }
      } else {
        // If it looks like a slug, try slug endpoint first, then fallback to ID
        console.log('ProductSlug looks like slug, trying slug endpoint first...');
        try {
          response = await axios.get(`/products/slug/${productSlug}`);
          console.log('Slug endpoint success:', response.data);
        } catch (err) {
          console.log('Slug endpoint failed:', err.response?.status, err.response?.data);
          if (err.response?.status === 404) {
            console.log('Trying ID endpoint as fallback...');
            // Try as ID for backward compatibility
            response = await axios.get(`/products/${productSlug}`);
            console.log('ID endpoint success:', response.data);
          } else {
            throw err;
          }
        }
      }

      // Handle both wrapped and direct response formats
      if (response.data.success && response.data.product) {
        setProduct(response.data.product);
        console.log('Product loaded successfully (wrapped format):', response.data.product);
      } else if (response.data._id) {
        // Direct product response
        setProduct(response.data);
        console.log('Product loaded successfully (direct format):', response.data);
      } else {
        throw new Error(response.data.message || 'Failed to load product');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError(error.response?.data?.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [productSlug]);

  useEffect(() => {
    if (storeSlug) {
      fetchProduct();
    }
  }, [productSlug, storeSlug, fetchProduct]);

  // GA4: Track product view when product loads
  useEffect(() => {
    if (product && !hasTrackedProduct.current) {
      trackViewItem({
        id: product._id || product.id,
        name: product.name,
        price: product.price,
        category: product.category,
        currency: storeCurrency || 'NGN'
      });
      hasTrackedProduct.current = true;
    }
  }, [product, trackViewItem]);

  // SEO: JSON-LD Structured Data + Meta Tags
  useEffect(() => {
    if (!product) return;

    // JSON-LD Product schema
    const availability = getStorefrontVariantAvailability(product);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || product.shortDescription || '',
      image: getAllImageUrls(product),
      sku: product.sku || product.id || product._id,
      offers: {
        '@type': 'Offer',
        price: parseFloat(product.price) || 0,
        priceCurrency: storeCurrency || 'NGN',
        availability: availability.hasStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: window.location.href
      }
    };
    if (product.category) jsonLd.category = product.category;

    let script = document.querySelector('#product-jsonld');
    if (!script) { script = document.createElement('script'); script.id = 'product-jsonld'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);

    // Meta tags
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${name}"], meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', product.shortDescription || product.description?.substring(0, 160) || product.name);
    setMeta('og:title', product.name);
    setMeta('og:description', product.shortDescription || product.description?.substring(0, 160) || '');
    const mainImage = getAllImageUrls(product)[0];
    if (mainImage) setMeta('og:image', mainImage);
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'product');

    return () => {
      const s = document.querySelector('#product-jsonld');
      if (s) s.remove();
      ['description', 'og:title', 'og:description', 'og:image', 'og:url', 'og:type'].forEach(n => {
        const el = document.querySelector(`meta[property="${n}"], meta[name="${n}"]`);
        if (el) el.remove();
      });
    };
  }, [product, storeCurrency]);

  // Helper function to check if product has variations
  // Supports both legacy array format and new variant matrix format
  const hasVariations = () => {
    if (!product?.hasVariations || !product?.variations) {
      return false;
    }

    // New variant matrix format: { attributes: [...], variants: [...] }
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      const result = Array.isArray(product.variations.attributes) &&
                     product.variations.attributes.length > 0 &&
                     Array.isArray(product.variations.variants) &&
                     product.variations.variants.length > 0;
      console.log('[ProductPage] hasVariations check (matrix format):', {
        hasVariations: product?.hasVariations,
        attributes: product?.variations?.attributes?.length,
        variants: product?.variations?.variants?.length,
        result
      });
      return result;
    }

    // Legacy array format: [{ name: "Size", values: [...] }]
    const result = Array.isArray(product.variations) && product.variations.length > 0;
    console.log('[ProductPage] hasVariations check (legacy format):', {
      hasVariations: product?.hasVariations,
      variationsLength: product?.variations?.length,
      result
    });
    return result;
  };

  // Helper to get variation attributes (works with both formats)
  const getVariationAttributes = () => {
    if (!product?.variations) return [];

    // New variant matrix format
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      return product.variations.attributes || [];
    }

    // Legacy format - convert to similar structure
    return product.variations.map(v => ({
      name: v.name,
      values: v.values?.map(val => typeof val === 'string' ? val : val.value) || []
    }));
  };

  // Helper to get variants (for new format) or null (for legacy)
  const getVariants = () => {
    if (!product?.variations) return null;

    // New variant matrix format
    if (typeof product.variations === 'object' && !Array.isArray(product.variations)) {
      return product.variations.variants || [];
    }

    return null; // Legacy format doesn't have separate variants array
  };

  // Find matching variant by selection (for new variant matrix format)
  const findVariantBySelection = (selection) => {
    const variants = getVariants();
    if (!variants) return null;

    return variants.find(variant => {
      if (!variant.combination) return false;
      const selectionKeys = Object.keys(selection);
      const combinationKeys = Object.keys(variant.combination);
      if (selectionKeys.length !== combinationKeys.length) return false;
      return selectionKeys.every(key => variant.combination[key] === selection[key]);
    }) || null;
  };

  // Helper function to get current price based on selected variations
  // Supports both legacy format and new variant matrix format
  const getCurrentPrice = () => {
    if (!hasVariations()) {
      return parseFloat(product?.price || 0);
    }

    const variants = getVariants();

    // New variant matrix format - use variants array for pricing
    if (variants && variants.length > 0) {
      // If all variations are selected, find the matching variant
      const attributes = getVariationAttributes();
      const allSelected = attributes.every(attr => selectedVariations[attr.name]);

      if (allSelected && Object.keys(selectedVariations).length > 0) {
        const matchingVariant = findVariantBySelection(selectedVariations);
        if (matchingVariant && matchingVariant.price !== undefined) {
          const varPrice = parseFloat(matchingVariant.price) || 0;
          console.log('[ProductPage] Using variant matrix price:', varPrice, 'for selection:', selectedVariations);
          return varPrice;
        }
      }

      // If not all selected, return minimum price from enabled variants
      const enabledVariants = variants.filter(v => v.enabled !== false);
      if (enabledVariants.length > 0) {
        const prices = enabledVariants
          .map(v => parseFloat(v.price) || 0)
          .filter(p => p > 0);
        if (prices.length > 0) {
          return Math.min(...prices);
        }
      }

      // Fallback to base product price
      return parseFloat(product?.price || 0);
    }

    // Legacy array format - use first variation type as primary price determinant
    const primaryVariation = product.variations[0];

    if (primaryVariation && selectedVariations[primaryVariation.name]) {
      const selectedValue = selectedVariations[primaryVariation.name];
      const valueObj = primaryVariation.values.find(v => v.value === selectedValue);

      if (valueObj && valueObj.price) {
        const varPrice = typeof valueObj.price === 'number' ? valueObj.price : parseFloat(valueObj.price);
        if (!isNaN(varPrice) && varPrice > 0) {
          console.log('[ProductPage] Using legacy combination price from', primaryVariation.name, ':', varPrice);
          return varPrice;
        }
      }
    }

    // If primary variation not selected, return the minimum available price from it
    if (primaryVariation && primaryVariation.values) {
      const allPrices = primaryVariation.values.map(v => {
        const price = typeof v.price === 'number' ? v.price : parseFloat(v.price);
        return !isNaN(price) && price > 0 ? price : 0;
      }).filter(p => p > 0);

      if (allPrices.length > 0) {
        return Math.min(...allPrices);
      }
    }

    // Fallback to base product price
    return parseFloat(product?.price || 0);
  };

  // Helper function to check if all variations are selected
  // Supports both legacy format and new variant matrix format
  const areAllVariationsSelected = () => {
    if (!hasVariations()) {
      return true; // No variations, so nothing to select
    }

    const attributes = getVariationAttributes();
    return attributes.every(attr => selectedVariations[attr.name]);
  };

  const selectedVariant = hasVariations() && areAllVariationsSelected()
    ? findStorefrontVariantBySelection(product, selectedVariations)
    : null;
  const selectedVariantAvailability = selectedVariant
    ? getStorefrontSelectedVariantAvailability(product, selectedVariations)
    : null;
  const storefrontAvailability = getStorefrontVariantAvailability(product);
  const activeAvailability = selectedVariantAvailability || storefrontAvailability;
  const availableQuantity = activeAvailability?.availableQuantity ?? Number(product?.stock ?? product?.quantity ?? 0);
  const isOutOfStock = availableQuantity <= 0;

  // Handler for variation selection
  const handleVariationChange = (type, value) => {
    console.log('[ProductPage] Variation selected:', { type, value });
    setSelectedVariations(prev => {
      const updated = {
        ...prev,
        [type]: value
      };
      console.log('[ProductPage] Updated selections:', updated);
      return updated;
    });
  };

  // Update display price whenever selections or product changes
  useEffect(() => {
    if (product) {
      const newPrice = getCurrentPrice();
      console.log('[ProductPage] Price updated:', newPrice);
      setCurrentDisplayPrice(newPrice);
    }
  }, [selectedVariations, product]);

  // Generate checkout QR code dynamically (includes selected variations)
  useEffect(() => {
    const generateCheckoutQR = async () => {
      if (!product || !storeSlug) return;

      // Only generate QR when all variations are selected (or no variations)
      if (hasVariations() && !areAllVariationsSelected()) {
        setCheckoutQR(null);
        return;
      }

      try {
        const productId = product.id || product._id;

        // Build checkout URL with product and variation data
        const baseUrl = window.location.origin;
        const params = new URLSearchParams({
          product: productId,
          qty: '1',
          utm_source: 'qr',
          utm_medium: 'qr_code',
          qr_type: 'PRODUCT',
          qr_id: productId
        });

        // Add selected variations if any
        if (hasVariations() && Object.keys(selectedVariations).length > 0) {
          params.set('variations', JSON.stringify(selectedVariations));
          // Also add the calculated price for the selected variation
          params.set('price', currentDisplayPrice.toString());
        }

        // QR points to the product page so the user lands here and uses the checkout modal
        const productPath = onSubdomain
          ? `/product/${product.slug || productSlug}`
          : `/s/${storeSlug}/product/${product.slug || productSlug}`;
        const checkoutUrl = `${baseUrl}${productPath}?${params.toString()}`;

        // Generate QR code as data URL
        const qrDataUrl = await QRCode.toDataURL(checkoutUrl, {
          width: 200,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' }
        });

        setCheckoutQR({
          success: true,
          qrCode: qrDataUrl,
          url: checkoutUrl,
          productId,
          hasVariations: hasVariations()
        });
      } catch (err) {
        console.log('[ProductPage] QR code generation error:', err.message);
        setCheckoutQR(null);
      }
    };
    generateCheckoutQR();
  }, [product, storeSlug, selectedVariations, currentDisplayPrice]);

  // Check if product is in wishlist (best-effort)
  useEffect(() => {
    const checkWishlist = async () => {
      try {
        const productId = product?._id || product?.id;
        if (!productId) return;
        // Use axios instance which handles custom domain routing correctly
        const resp = await axios.get('/customer/dashboard/wishlist');
        if (resp.data) {
          const exists = (resp.data.items || []).some(i => i.productId === productId || i.product?.id === productId);
          setInWishlist(exists);
        }
      } catch (_) {}
    };
    checkWishlist();
  }, [product?._id, product?.id]);

  // Fetch cart on mount
  useEffect(() => {
    dispatch(fetchCart(customerId));
  }, [dispatch, customerId]);

  // Visit tracking effect
  useEffect(() => {
    if (product && product.storeId) {
      const trackVisitData = async () => {
        try {
          const visitData = await visitTracker.trackVisit(
            product.merchantId || product.storeId,
            product.storeId,
            window.location.pathname
          );

          if (visitData) {
            dispatch(trackVisit(visitData));
          }
        } catch (error) {
          console.error('Error tracking visit:', error);
        }
      };

      trackVisitData();
    }
  }, [product, dispatch]);

  // Update visit duration on page visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && visitTracker.isTracking) {
        const duration = visitTracker.updateDuration();
        dispatch(updateVisitDuration({
          sessionId: visitTracker.sessionId,
          duration
        }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch]);


  // Fetch related products - first tries merchant-defined collection, then falls back to category-based
  const fetchRelatedProducts = async (productData) => {
    try {
      // First, try to get merchant-defined related products
      const productId = productData._id || productData.id;

      try {
        const response = await axios.get(`/products/${productId}/related?limit=4`);
        if (response.data.success && response.data.products?.length > 0) {
          // Filter out the current product
          const filtered = response.data.products.filter(p =>
            (p._id || p.id) !== productId
          );
          setRelatedProducts(filtered);
          return;
        }
      } catch (err) {
        // If endpoint doesn't exist or returns nothing, fall back to category-based
        console.log('No merchant-defined related products, falling back to category');
      }

      // Fallback: Get products from the same category
      if (productData.category) {
        const response = await axios.get(`/products/public?category=${encodeURIComponent(productData.category)}&limit=5&storeSlug=${storeSlug}`);
        const products = response.data.products || response.data || [];
        // Filter out the current product
        const filtered = products.filter(p =>
          (p._id || p.id) !== productId
        ).slice(0, 4);
        setRelatedProducts(filtered);
      }
    } catch (err) {
      console.error('Error fetching related products:', err);
    }
  };

  // Fetch related products when product loads
  useEffect(() => {
    if (product && storeSlug) {
      fetchRelatedProducts(product);
    }
  }, [product, storeSlug]);

  // Deep-link intent from marketplace: ?buy=now auto-adds the product to cart
  // and opens the cart flyout. Only fires for products with no required
  // variations; variation products still need explicit selection.
  const autoAddedRef = useRef(false);
  useEffect(() => {
    if (autoAddedRef.current) return;
    if (!product) return;
    if (searchParams.get('buy') !== 'now') return;
    if (hasVariations && hasVariations()) return;

    autoAddedRef.current = true;
    handleAddToCart()
      .then(() => {
        if (typeof onOpenCart === 'function') onOpenCart();
      })
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('buy');
        next.delete('source');
        setSearchParams(next, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, searchParams]);

  const handleAddToCart = async () => {
    if (product) {
      const currentPrice = getCurrentPrice();
      const finalQuantity = isUnitBased() ? unitQuantity : quantity;

      // Build variation description for cart display
      let variationText = '';
      if (hasVariations() && Object.keys(selectedVariations).length > 0) {
        variationText = Object.entries(selectedVariations)
          .map(([type, value]) => `${type}: ${value}`)
          .join(', ');
      }

      // Find matching variant SKU if applicable
      let variantSku;
      let variantId;
      let variantName;
      let variantCombination;
      if (hasVariations() && Object.keys(selectedVariations).length > 0) {
        const matchingVariant = findVariantBySelection(selectedVariations);
        const variantIdentity = getStorefrontVariantIdentity(matchingVariant);
        if (matchingVariant?.sku) {
          variantSku = matchingVariant.sku;
        }
        if (variantIdentity?.variantId) variantId = variantIdentity.variantId;
        if (variantIdentity?.variantName) variantName = variantIdentity.variantName;
        if (variantIdentity?.variantCombination) variantCombination = variantIdentity.variantCombination;
      }

      // Add the product to cart with the selected quantity and variations
      dispatch(addToCart({
        product: {
          _id: product._id || product.id,
          id: product.id || product._id,
          name: product.name,
          price: currentPrice,
          image: product.primaryImage || product.imageUrl || product.images?.[0],
          storeSlug: storeSlug,
          variations: hasVariations() ? selectedVariations : undefined,
          variationText: variationText || undefined,
          variantId: variantId || undefined,
          variantSku: variantSku || undefined,
          variantName: variantName || variationText || undefined,
          variantCombination: variantCombination || undefined,
          // Unit-based selling info
          sellByUnit: isUnitBased() ? true : undefined,
          unitOfMeasure: isUnitBased() ? product.unitOfMeasure : undefined,
          pricePerUnit: isUnitBased() ? currentPrice : undefined,
          unitQuantity: isUnitBased() ? unitQuantity : undefined
        },
        quantity: finalQuantity
      }));

      // Open checkout modal
      if (onOpenCart) onOpenCart();

      // GA4: Track add to cart
      trackAddToCart({
        id: product._id || product.id,
        name: product.name,
        price: currentPrice,
        category: product.category,
        currency: storeCurrency || 'NGN'
      }, finalQuantity);

      // Track conversion
      if (visitTracker.isTracking) {
        const conversionData = visitTracker.markConversion('purchase', currentPrice * finalQuantity);
        dispatch(markConversion(conversionData));
      }

      // Track abandoned cart
      try {
        let sessionId = sessionStorage.getItem('cart_session_id');
        if (!sessionId) {
          sessionId = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
          sessionStorage.setItem('cart_session_id', sessionId);
        }
        console.log('[AbandonedCart] Tracking cart:', { sessionId, storeSlug, productId: product.id || product._id });
        const trackResponse = await axios.post('/abandoned-carts/track', {
          sessionId,
          items: [{
            productId: product.id || product._id,
            quantity: quantity,
            price: currentPrice,
            name: product.name,
            image: product.primaryImage || product.imageUrl || product.images?.[0] || ''
          }],
          storeSlug: storeSlug,
          subtotal: currentPrice * quantity,
          currency: storeCurrency || 'NGN'
        });
        console.log('[AbandonedCart] Track response:', trackResponse.data);
      } catch (e) {
        console.error('[AbandonedCart] Track error:', e.message, e.response?.data);
      }
    }
  };

  const toggleWishlist = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const productId = product?._id || product?.id;
    if (!productId) return;

    setWishlistLoading(true);
    try {
      // Use axios instance which handles custom domain routing correctly
      const response = await axios.post('/customer/wishlist/toggle', { productId });

      if (response.data) {
        setInWishlist(response.data.inWishlist);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // User not logged in - redirect to customer app login
        // Use getCustomerBaseUrl() to get the correct customer app URL
        // This works for platform subdomains AND custom domains
        const currentUrl = window.location.href;
        const customerBaseUrl = getCustomerBaseUrl();

        console.log('[ProductPage] Wishlist auth required, redirecting to:', customerBaseUrl);
        console.log('[ProductPage] Return URL:', currentUrl);
        console.log('[ProductPage] Store slug:', storeSlug);

        // Redirect to customer app login with return URL and wishlist product
        const loginUrl = `${customerBaseUrl}/login?redirect=${encodeURIComponent(currentUrl)}&wishlist=${productId}&store=${storeSlug}`;
        window.location.href = loginUrl;
        return;
      } else {
        console.error('Wishlist toggle error:', error);
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    const maxStock = availableQuantity ?? product?.stock ?? product?.quantity ?? 99;
    if (newQuantity >= 1 && newQuantity <= maxStock) {
      setQuantity(newQuantity);
    }
  };

  // Handle unit-based quantity change
  const handleUnitQuantityChange = (newQty) => {
    setUnitQuantity(newQty);
  };

  // Check if product is sold by unit
  const isUnitBased = () => {
    return product?.sellByUnit === true && product?.unitOfMeasure && product?.unitOfMeasure !== 'PIECE';
  };

  const handleWhatsAppOrder = () => {
    if (!product || !isWhatsAppOrdersEnabled(store)) return;
    setShowWhatsAppModal(true);
  };

  const getMaxQuantity = () => {
    return availableQuantity ?? product?.stock ?? product?.quantity ?? 99;
  };

  // --- Loading State ---
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: themeStyles?.background || '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #f3f4f6',
            borderTopColor: themeStyles?.text || '#111827',
            borderRadius: '50%',
            margin: '0 auto 16px auto',
            animation: 'productPageSpin 0.8s linear infinite'
          }} />
          <style>{`@keyframes productPageSpin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: themeStyles?.textMuted || '#6b7280', fontSize: '15px', margin: 0 }}>Loading product...</p>
        </div>
      </div>
    );
  }

  // --- Error State ---
  if (error || !product) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: themeStyles?.background || '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '0 24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px auto'
          }}>
            <svg width="28" height="28" fill="none" stroke="#ef4444" viewBox="0 0 24 24" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path strokeLinecap="round" d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: themeStyles?.text || '#111827', margin: '0 0 8px 0' }}>
            Product Not Found
          </h1>
          <p style={{ color: themeStyles?.textMuted || '#6b7280', fontSize: '15px', margin: '0 0 4px 0' }}>
            The product you're looking for doesn't exist.
          </p>
          <p style={{ color: themeStyles?.textMuted || '#9ca3af', fontSize: '13px', margin: '0 0 24px 0' }}>
            URL: /s/{storeSlug}/product/{productSlug}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Button onClick={() => navigate(getStorePath())}>
              Back to Store
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
            >
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is an appointment product (case-insensitive)
  const isAppointment =
    product.productType?.toUpperCase() === 'APPOINTMENT' ||
    product.category?.toLowerCase() === 'appointment';

  // If it's an appointment product, render the modern inline booking experience
  if (isAppointment) {
    return (
      <AppointmentServicePage
        product={product}
        store={store}
        themeStyles={themeStyles}
        getStorePath={getStorePath}
      />
    );
  }

  const images = getAllImageUrls(product);

  // Build a unified media gallery: images first, then the product video if
  // the merchant uploaded one (stored on `digitalSettings.video.url` by the
  // product form). The video gets the primary image as its poster so the
  // swap from photo → player is visually continuous instead of a black flash.
  const videoUrl = product?.digitalSettings?.video?.url || null;
  const media = [
    ...images.map((url) => ({ type: 'image', url })),
    ...(videoUrl ? [{ type: 'video', url: videoUrl, poster: images[0] || null }] : []),
  ];
  const activeMedia = media[selectedImage] || { type: 'image', url: getFallbackImageUrl() };

  // Determine primary color from theme
  // Merchant's brand color — used for the price emphasis and variant
  // selection borders. Brand colors deliberately stay put across light
  // and dark mode (only neutrals swap), so a single resolve works for
  // both. The legacy fallback `'#111827'` was a bug — the source fields
  // are `themeStyles.primary` / `theme.colors.primary`, not `*.primaryColor`.
  const primaryColor = themeStyles?.primary || theme?.colors?.primary || '#0d7463';
  const productShareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${location.pathname}`
    : '';
  const productShareText = `Check out ${product.name} from ${store?.name || 'this store'}`;
  const encodedShareUrl = encodeURIComponent(productShareUrl);
  const encodedShareText = encodeURIComponent(productShareText);

  const handleCopyProductLink = async () => {
    if (!productShareUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(productShareUrl);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1800);
    } catch (copyError) {
      console.warn('Unable to copy product link:', copyError);
    }
  };

  const handleNativeProductShare = async () => {
    if (!productShareUrl) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: productShareText,
          url: productShareUrl
        });
        return;
      } catch (shareError) {
        if (shareError?.name === 'AbortError') return;
      }
    }
    handleCopyProductLink();
  };

  const shareActionBaseStyle = {
    width: '34px',
    height: '34px',
    maxWidth: '34px',
    maxHeight: '34px',
    borderRadius: '999px',
    border: `1px solid ${themeStyles?.border || 'var(--sf-border, #e5e7eb)'}`,
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    padding: 0,
    minWidth: '34px',
    lineHeight: 0,
    boxSizing: 'border-box',
    appearance: 'none',
    flex: '0 0 34px'
  };

  const productShareToolbar = (
    <div
      className="product-share-strip"
      aria-label="Share product"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      <button
        type="button"
        className="product-share-action"
        onClick={handleNativeProductShare}
        aria-label="Share product"
        title="Share product"
        style={{ ...shareActionBaseStyle, color: primaryColor }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      </button>
      <a
        className="product-share-action"
        href={`https://wa.me/?text=${encodedShareText}%20${encodedShareUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
        style={{ ...shareActionBaseStyle, color: '#128c7e' }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.52 3.48A11.84 11.84 0 0012.08 0C5.48 0 .11 5.36.1 11.96c0 2.11.55 4.18 1.61 6.01L0 24l6.18-1.62a11.95 11.95 0 005.89 1.5h.01c6.6 0 11.97-5.36 11.98-11.96a11.85 11.85 0 00-3.54-8.44zM12.08 21.86h-.01a9.9 9.9 0 01-5.04-1.38l-.36-.21-3.67.96.98-3.57-.23-.37a9.88 9.88 0 01-1.52-5.32c0-5.43 4.42-9.85 9.86-9.85a9.78 9.78 0 016.96 2.89 9.78 9.78 0 012.88 6.97c-.01 5.43-4.43 9.84-9.85 9.84zm5.4-7.37c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z" />
        </svg>
      </a>
      <a
        className="product-share-action"
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
        title="Share on Facebook"
        style={{ ...shareActionBaseStyle, color: '#1877f2' }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.96h-1.52c-1.49 0-1.96.93-1.96 1.88v2.27h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z" />
        </svg>
      </a>
      <a
        className="product-share-action"
        href={`https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedShareUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
        title="Share on X"
        style={{ ...shareActionBaseStyle, color: themeStyles?.text || 'var(--sf-text, #111827)', fontSize: '12px', fontWeight: 800 }}
      >
        X
      </a>
      <button
        type="button"
        className="product-share-action"
        onClick={handleCopyProductLink}
        aria-label="Copy product link"
        title="Copy product link"
        style={{ ...shareActionBaseStyle, color: shareCopied ? primaryColor : (themeStyles?.textMuted || 'var(--sf-text-muted, #6b7280)') }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
      </button>
      {shareCopied && (
        <span style={{
          fontSize: '12px',
          fontWeight: 700,
          color: primaryColor,
          lineHeight: 1
        }}>
          Copied
        </span>
      )}
    </div>
  );

  return (
    <div className={isEditionTheme ? 'edition-product' : isAtelierTheme ? 'atelier-product' : ''}>
      <style>{`
        @keyframes productPageSpin { to { transform: rotate(360deg); } }

        .product-share-action {
          width: 34px !important;
          height: 34px !important;
          min-width: 34px !important;
          max-width: 34px !important;
          min-height: 34px !important;
          max-height: 34px !important;
          flex: 0 0 34px !important;
          border-radius: 999px !important;
          box-sizing: border-box !important;
          padding: 0 !important;
          line-height: 0 !important;
        }

        /* Mobile Optimizations for Product Page */
        @media (max-width: 768px) {
          .appointment-product-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }

          .product-main-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }

          .product-header-container {
            padding: 0 16px !important;
          }

          .product-main-container {
            padding: 32px 16px !important;
          }

          .product-title {
            font-size: 24px !important;
          }

          .product-price {
            font-size: 24px !important;
          }

          .product-image-thumbnails {
            gap: 8px !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            max-width: 100% !important;
            scrollbar-width: thin !important;
            justify-content: flex-start !important;
          }

          .product-thumbnail {
            width: 56px !important;
            height: 56px !important;
            flex-shrink: 0 !important;
          }

          .product-variations-container {
            gap: 12px !important;
          }

          .variation-button {
            padding: 10px 16px !important;
            min-height: 44px !important;
          }

          .quantity-selector-button {
            width: 44px !important;
            height: 44px !important;
          }

          .add-to-cart-button {
            width: 100% !important;
            min-height: 48px !important;
            padding: 14px 24px !important;
          }

          .wishlist-button {
            min-height: 44px !important;
          }

          .product-actions-container {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            align-items: center !important;
          }

          .product-actions-container .add-to-cart-button,
          .product-actions-container .whatsapp-order-button,
          .product-actions-container .wishlist-button {
            flex: 0 0 auto !important;
            width: auto !important;
            min-width: 0 !important;
          }

          .product-share-strip {
            gap: 8px !important;
          }

          .product-share-action {
            width: 34px !important;
            height: 34px !important;
            min-width: 34px !important;
            max-width: 34px !important;
            min-height: 34px !important;
            max-height: 34px !important;
            flex: 0 0 34px !important;
            border-radius: 999px !important;
            box-sizing: border-box !important;
            padding: 0 !important;
          }

          .product-description-section {
            margin-top: 48px !important;
            padding: 24px 16px !important;
          }

          .related-products-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 16px !important;
          }

          .product-details-section {
            gap: 24px !important;
          }
        }

        @media (max-width: 480px) {
          .product-main-container {
            padding: 24px 12px !important;
          }

          .product-title {
            font-size: 22px !important;
          }

          .product-price {
            font-size: 22px !important;
          }

          .product-short-description {
            font-size: 15px !important;
          }

          .product-description-section {
            padding: 20px 12px !important;
          }

          .related-products-grid {
            grid-template-columns: 1fr !important;
          }

          .product-thumbnail {
            width: 48px !important;
            height: 48px !important;
          }
        }

        /* ─── Video thumbnail play badge ──────────────────────────────
           Subtle pill centered on the video thumbnail. Neutral dark
           backdrop, white triangle — no gradients, no glow. Reads as
           "play me" at a glance without shouting. */
        .product-thumbnail-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }
        .product-thumbnail-play::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(17, 24, 39, 0.28);
        }
        .product-thumbnail-play svg {
          position: relative;
          color: #ffffff;
          width: 18px;
          height: 18px;
          padding: 4px;
          background: rgba(17, 24, 39, 0.88);
          border-radius: 999px;
          box-sizing: content-box;
        }

        /* ─── Rendered HTML description (AI-generated structured copy) ───
           Customers see this on the storefront. Headings, lists, and
           specifications tables get polished typography so the AI output
           reads like a real landing page. Uses --sf-* tokens so the
           whole block flips with dark mode automatically. */
        .product-description-body > *:first-child { margin-top: 0; }
        .product-description-body > *:last-child { margin-bottom: 0; }
        .product-description-body p { margin: 0 0 14px; color: var(--sf-text); }
        .product-description-body h3 {
          font-size: 13px;
          font-weight: 700;
          color: var(--sf-text);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin: 20px 0 10px;
        }
        .product-description-body ul,
        .product-description-body ol {
          margin: 0 0 16px;
          padding-left: 22px;
          color: var(--sf-text);
        }
        .product-description-body li { margin-bottom: 6px; }
        .product-description-body strong { color: var(--sf-text); font-weight: 600; }
        .product-description-body table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0 16px;
          background: var(--sf-surface);
          color: var(--sf-text);
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid var(--sf-border);
          font-size: 14px;
        }
        .product-description-body th,
        .product-description-body td {
          padding: 10px 14px;
          text-align: left;
          border-bottom: 1px solid var(--sf-border);
        }
        .product-description-body th {
          font-weight: 600;
          background: var(--sf-surface-2);
          color: var(--sf-text);
        }
        .product-description-body tr:last-child th,
        .product-description-body tr:last-child td { border-bottom: 0; }
      `}</style>
      <div className={`product-page-shell ${isEditionTheme ? 'edition-product' : ''} ${isAtelierTheme ? 'atelier-product' : ''} ${isGalleryTheme ? 'gallery-product' : ''}`} style={{ minHeight: '100vh', backgroundColor: themeStyles?.background || '#ffffff' }}>

      {/* Breadcrumb Navigation */}
      <div className="product-breadcrumb" style={{
        backgroundColor: themeStyles?.background || '#ffffff',
        borderBottom: `1px solid ${themeStyles?.border || '#f3f4f6'}`,
        padding: '14px 0'
      }}>
        <div className="product-header-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', flexWrap: 'wrap' }}>
            <span
              onClick={() => navigate(getStorePath())}
              style={{ color: themeStyles?.textMuted || '#6b7280', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={(e) => { e.target.style.color = themeStyles?.text || '#111827'; }}
              onMouseLeave={(e) => { e.target.style.color = themeStyles?.textMuted || '#6b7280'; }}
            >
              Home
            </span>
            <svg width="14" height="14" fill="none" stroke="#9ca3af" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <span
              onClick={() => navigate(getStorePath('shop'))}
              style={{ color: themeStyles?.textMuted || '#6b7280', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={(e) => { e.target.style.color = themeStyles?.text || '#111827'; }}
              onMouseLeave={(e) => { e.target.style.color = themeStyles?.textMuted || '#6b7280'; }}
            >
              Shop
            </span>
            {product.category && (
              <>
                <svg width="14" height="14" fill="none" stroke="#9ca3af" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <span
                  onClick={() => navigate(getStorePath(`category/${product.category}`))}
                  style={{ color: themeStyles?.textMuted || '#6b7280', cursor: 'pointer', transition: 'color 0.15s' }}
                  onMouseEnter={(e) => { e.target.style.color = themeStyles?.text || '#111827'; }}
                  onMouseLeave={(e) => { e.target.style.color = themeStyles?.textMuted || '#6b7280'; }}
                >
                  {product.category}
                </span>
              </>
            )}
            <svg width="14" height="14" fill="none" stroke="#9ca3af" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <span style={{ color: themeStyles?.text || '#374151', fontWeight: '600' }}>
              {product.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="product-main-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 24px' }}>
        <div className="product-main-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '64px',
          alignItems: 'start'
        }}>
          {/* Product Media (images + optional video) */}
          <div className="product-media-section" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Main viewer */}
            <div className={`product-main-viewer ${mediaReady ? 'product-main-viewer--ready' : 'product-main-viewer--loading'}`} style={{
              aspectRatio: '1',
              backgroundColor: activeMedia.type === 'video' ? '#000' : '#f9fafb',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {activeMedia.type === 'video' ? (
                <video
                  key={activeMedia.url}
                  src={activeMedia.url}
                  poster={activeMedia.poster || undefined}
                  controls
                  controlsList="nodownload"
                  playsInline
                  preload="metadata"
                  onLoadedData={() => setMediaReady(true)}
                  onError={() => setMediaReady(true)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    background: '#000'
                  }}
                />
              ) : (
                <img
                  src={activeMedia.url || getFallbackImageUrl()}
                  alt={product.name}
                  loading={selectedImage === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onLoad={() => setMediaReady(true)}
                  onError={(e) => {
                    e.target.src = getFallbackImageUrl();
                    setMediaReady(true);
                  }}
                />
              )}
            </div>

            {/* Thumbnails */}
            {media.length > 1 && (
              <div className="product-image-thumbnails" style={{
                display: 'flex',
                gap: '10px',
                overflowX: 'auto',
                paddingBottom: '4px'
              }}>
                {media.map((item, index) => (
                  <button
                    key={`${item.type}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className="product-thumbnail"
                    aria-label={item.type === 'video' ? 'Product video' : `Product image ${index + 1}`}
                    style={{
                      width: '64px',
                      height: '64px',
                      minWidth: '64px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: selectedImage === index ? '2px solid #111827' : '2px solid transparent',
                      cursor: 'pointer',
                      background: themeStyles?.backgroundAlt || '#f9fafb',
                      padding: 0,
                      position: 'relative',
                      transform: selectedImage === index ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                      opacity: selectedImage === index ? 1 : 0.7
                    }}
                  >
                    <img
                      src={(item.type === 'video' ? item.poster : item.url) || '/placeholder-product.jpg'}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {item.type === 'video' && (
                      <span className="product-thumbnail-play" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {!isEditionTheme && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {productShareToolbar}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="product-details-section" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Title */}
            <div className="product-summary-header" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h1 className="product-title" style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: themeStyles?.text || '#111827',
                lineHeight: '1.2',
                margin: 0
              }}>
                {product.name}
              </h1>

              {product.activeSale && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    padding: '6px 11px',
                    borderRadius: '999px',
                    backgroundColor: 'rgba(13, 116, 99, 0.1)',
                    color: '#0d7463',
                    border: '1px solid rgba(13, 116, 99, 0.22)',
                    fontSize: '12px',
                    fontWeight: '800',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase'
                  }}>
                    {product.activeSale.badgeText || product.activeSale.name || 'Sale'}
                  </span>
                </div>
              )}

              {/* Price */}
              <div className="product-price" style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: primaryColor,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                {hasVariations() && Object.keys(selectedVariations).length === 0 ? (
                  // Show price range when variations exist but none selected
                  (() => {
                    const variants = getVariants();

                    // New variant matrix format - get prices from variants array
                    if (variants && variants.length > 0) {
                      const enabledVariants = variants.filter(v => v.enabled !== false);
                      const prices = enabledVariants
                        .map(v => parseFloat(v.price) || 0)
                        .filter(p => p > 0);

                      if (prices.length > 0) {
                        const minPrice = Math.min(...prices);
                        const maxPrice = Math.max(...prices);

                        if (minPrice === maxPrice) {
                          return currency.format(minPrice);
                        }
                        return `${currency.format(minPrice)} - ${currency.format(maxPrice)}`;
                      }
                    }

                    // Legacy format - get prices from first variation type
                    const primaryVariation = product.variations[0];
                    if (primaryVariation && primaryVariation.values) {
                      const prices = primaryVariation.values
                        .map(v => typeof v.price === 'number' ? v.price : parseFloat(v.price))
                        .filter(p => !isNaN(p) && p > 0);

                      if (prices.length > 0) {
                        const minPrice = Math.min(...prices);
                        const maxPrice = Math.max(...prices);

                        if (minPrice === maxPrice) {
                          return currency.format(minPrice);
                        }
                        return `${currency.format(minPrice)} - ${currency.format(maxPrice)}`;
                      }
                    }
                    return currency.format(parseFloat(product?.price || 0));
                  })()
                ) : (
                  <>
                    {product.compareAtPrice && parseFloat(product.compareAtPrice) > (currentDisplayPrice || getCurrentPrice()) && (
                      <span style={{
                        fontSize: '1.1rem',
                        fontWeight: '500',
                        color: themeStyles?.textMuted || '#9ca3af',
                        textDecoration: 'line-through'
                      }}>
                        {currency.format(parseFloat(product.compareAtPrice))}
                      </span>
                    )}
                    <span>{currency.format(currentDisplayPrice || getCurrentPrice())}</span>
                    {product.compareAtPrice && parseFloat(product.compareAtPrice) > (currentDisplayPrice || getCurrentPrice()) && (
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#16a34a',
                        backgroundColor: '#dcfce7',
                        padding: '4px 10px',
                        borderRadius: '999px'
                      }}>
                        {Math.round((1 - (currentDisplayPrice || getCurrentPrice()) / parseFloat(product.compareAtPrice)) * 100)}% OFF
                      </span>
                    )}
                  </>
                )}
              </div>
              {hasVariations() && !areAllVariationsSelected() && (
                <span style={{ fontSize: '14px', color: themeStyles?.textMuted || '#6b7280' }}>
                  Select options to see final price
                </span>
              )}

              {/* Stock Status Badge */}
              <div className="product-stock-meta" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {isOutOfStock ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 12px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: '#fef2f2',
                    color: '#dc2626'
                  }}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Out of Stock
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 12px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: '#dcfce7',
                    color: '#166534'
                  }}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    In Stock ({availableQuantity} available)
                  </span>
                )}
                {product.category && (
                  <span
                    onClick={() => navigate(getStorePath(`category/${product.category}`))}
                    style={{
                      fontSize: '13px',
                      color: themeStyles?.textMuted || '#6b7280',
                      cursor: 'pointer',
                      padding: '5px 12px',
                      borderRadius: '999px',
                      backgroundColor: themeStyles?.backgroundAlt || '#f3f4f6',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = themeStyles?.border || '#e5e7eb'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = themeStyles?.backgroundAlt || '#f3f4f6'; }}
                  >
                    {product.category}
                  </span>
                )}
              </div>
              <ProductViewerCount
                storeSlug={storeSlug}
                productId={product?.id || product?._id}
                enabled={store?.settings?.productViewerCount?.enabled === true}
                message={store?.settings?.productViewerCount?.message}
              />
            </div>

            {/* Short Description */}
            {product.shortDescription && (
              <p className="product-short-description" style={{
                fontSize: '1rem',
                color: themeStyles?.textMuted || '#6b7280',
                lineHeight: '1.6',
                margin: 0
              }}>
                {product.shortDescription}
              </p>
            )}

            {/* Divider */}
            <div className="product-details-divider" style={{ borderTop: '1px solid #f3f4f6' }} />

            {/* Product Variations */}
            {hasVariations() && (
              <div className="product-variations-container" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '12px'
              }}>
                {(() => {
                  const attributes = getVariationAttributes();
                  const variants = getVariants();
                  const isMatrixFormat = variants !== null;

                  return attributes.map((attr, idx) => {
                    // For legacy format, check if this attribute has different prices
                    const hasDifferentPrices = (() => {
                      if (isMatrixFormat) return false; // Matrix format shows prices differently

                      // Legacy format - check variation values for prices
                      const legacyVariation = product.variations[idx];
                      if (!legacyVariation?.values || legacyVariation.values.length < 2) return false;

                      const prices = legacyVariation.values
                        .map(v => typeof v === 'object' && v.price ? parseFloat(v.price) : 0)
                        .filter(p => !isNaN(p) && p > 0);

                      if (prices.length === 0) return false;

                      const minPrice = Math.min(...prices);
                      const maxPrice = Math.max(...prices);

                      return minPrice !== maxPrice;
                    })();

                    // Get values for this attribute
                    const attrValues = attr.values || [];

                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                        <label style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: themeStyles?.text || '#374151',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase'
                        }}>
                          {attr.name}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={selectedVariations[attr.name] || ''}
                            onChange={(e) => handleVariationChange(attr.name, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px 32px 10px 12px',
                              border: selectedVariations[attr.name] ? `2px solid ${primaryColor}` : '1.5px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: themeStyles?.text || '#111827',
                              backgroundColor: selectedVariations[attr.name] ? `${primaryColor}08` : '#fff',
                              cursor: 'pointer',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              transition: 'all 0.15s ease',
                              outline: 'none'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = primaryColor;
                              e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = selectedVariations[attr.name] ? primaryColor : '#d1d5db';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <option value="">Select</option>
                            {attrValues.map((valueItem, vIdx) => {
                              // Handle both string values (matrix format) and object values (legacy format)
                              const valueStr = typeof valueItem === 'string' ? valueItem : valueItem.value;
                              // For legacy format, get price from value object
                              const showPrice = hasDifferentPrices && typeof valueItem === 'object' && valueItem.price;

                              return (
                                <option key={vIdx} value={valueStr}>
                                  {valueStr}{showPrice ? ` (${currency.format(parseFloat(valueItem.price))})` : ''}
                                </option>
                              );
                            })}
                          </select>
                          {/* Dropdown arrow icon */}
                          <svg
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: themeStyles?.textMuted || '#6b7280',
                              pointerEvents: 'none'
                            }}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className={`product-purchase-panel ${isUnitBased() ? 'product-purchase-panel--unit' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Quantity Selector - Show UnitQuantityInput for unit-based products */}
              {isUnitBased() ? (
                <UnitQuantityInput
                  unitOfMeasure={product.unitOfMeasure}
                  pricePerUnit={currentDisplayPrice || product.price}
                  quantity={unitQuantity}
                  onQuantityChange={handleUnitQuantityChange}
                  minQuantity={parseFloat(product.minOrderQuantity) || 0.1}
                  stepIncrement={parseFloat(product.stepIncrement) || 0.1}
                  formatPrice={currency.format}
                  primaryColor={primaryColor || '#0d7463'}
                />
              ) : (
                <div className="product-quantity-control" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: themeStyles?.text || '#374151' }}>Quantity</span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      className="quantity-selector-button"
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: themeStyles?.textMuted || '#6b7280',
                        background: 'none',
                        border: 'none',
                        cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                        opacity: quantity <= 1 ? 0.4 : 1,
                        transition: 'opacity 0.15s'
                      }}
                      disabled={quantity <= 1}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <span style={{
                      width: '48px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: themeStyles?.text || '#111827',
                      borderLeft: '1px solid #e5e7eb',
                      borderRight: '1px solid #e5e7eb'
                    }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(1)}
                      className="quantity-selector-button"
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: themeStyles?.textMuted || '#6b7280',
                        background: 'none',
                        border: 'none',
                        cursor: quantity >= getMaxQuantity() ? 'not-allowed' : 'pointer',
                        opacity: quantity >= getMaxQuantity() ? 0.4 : 1,
                        transition: 'opacity 0.15s'
                      }}
                      disabled={quantity >= getMaxQuantity()}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="product-actions-container" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center'
              }}>
                {/* Add to Cart - Primary Button */}
                  <button
                    onClick={handleAddToCart}
                  disabled={isOutOfStock || !areAllVariationsSelected()}
                  className="add-to-cart-button"
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: (isOutOfStock || !areAllVariationsSelected()) ? 'not-allowed' : 'pointer',
                    backgroundColor: (isOutOfStock || !areAllVariationsSelected()) ? (themeStyles?.border || '#e5e7eb') : primaryColor,
                    color: (isOutOfStock || !areAllVariationsSelected()) ? (themeStyles?.textMuted || '#9ca3af') : '#ffffff',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = themeStyles?.primaryHover || primaryColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = primaryColor;
                    }
                  }}
                >
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {isOutOfStock
                    ? 'Out of Stock'
                    : !areAllVariationsSelected()
                      ? 'Select Options'
                      : 'Add to Cart'}
                </button>

                {/* WhatsApp Order Button */}
                {isWhatsAppOrdersEnabled(store) && (
                  <button
                    onClick={handleWhatsAppOrder}
                    disabled={isOutOfStock || !areAllVariationsSelected()}
                    className="whatsapp-order-button"
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: 'none',
                      cursor: (isOutOfStock || !areAllVariationsSelected()) ? 'not-allowed' : 'pointer',
                      backgroundColor: (isOutOfStock || !areAllVariationsSelected()) ? '#e5e7eb' : '#25D366',
                      color: (isOutOfStock || !areAllVariationsSelected()) ? '#9ca3af' : '#ffffff',
                      transition: 'all 0.2s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = '#20bd5a';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = '#25D366';
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                )}

                {/* Save/Wishlist Button */}
                <button
                  onClick={toggleWishlist}
                  disabled={wishlistLoading}
                  className="wishlist-button"
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: inWishlist ? '1.5px solid #f59e0b' : '1.5px solid #d1d5db',
                    background: inWishlist ? '#fffbeb' : '#ffffff',
                    color: inWishlist ? '#d97706' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: wishlistLoading ? 'wait' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!inWishlist) {
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!inWishlist) {
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                >
                  <svg width="18" height="18" fill={inWishlist ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {wishlistLoading ? '...' : inWishlist ? 'Saved' : 'Save'}
                </button>
              </div>
            </div>
            {isEditionTheme && (
              <div className="edition-product-share">
                <span>Share</span>
                {productShareToolbar}
              </div>
            )}
          </div>
        </div>

        {/* Product Description - Below the two columns.
            Descriptions can now contain structured HTML (intro <p>, Key
            features <ul>, Specifications <table>) produced by the AI
            generator. Backend sanitises before save; we apply a small
            belt-and-braces scrub on render. For the "Read more" preview we
            strip tags so we don't chop mid-element. */}
        {product.description && (() => {
          const html = product.description;
          // Strip tags + collapse whitespace for the truncated preview.
          const plain = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
          const isLong = plain.length > 200;
          // Strip script/style/event-handler attributes as a courtesy.
          const safe = html
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/ on[a-z]+="[^"]*"/gi, '')
            .replace(/ on[a-z]+='[^']*'/gi, '')
            .replace(/javascript:/gi, '');
          return (
            <div className="product-description-section" style={{
              marginTop: '56px',
              padding: '32px',
              backgroundColor: themeStyles?.backgroundAlt || '#f9fafb',
              borderRadius: '16px'
            }}>
              {isEditionTheme ? (
                <>
                  <button
                    className="edition-description-toggle"
                    onClick={() => setShowFullDescription((v) => !v)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: primaryColor,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      width: '100%',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>Description</span>
                    <svg width="17" height="17" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d={showFullDescription ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                    </svg>
                  </button>
                  {showFullDescription && (
                    <div className="product-description-body" style={{ color: '#4b5563', lineHeight: '1.7', fontSize: '15px' }} dangerouslySetInnerHTML={{ __html: safe }} />
                  )}
                </>
              ) : (
                <>
                  <h3 className="product-description-title" style={{ fontSize: '20px', fontWeight: '700', color: themeStyles?.text || '#111827', margin: '0 0 16px 0' }}>Description</h3>
                  <div className="product-description-body" style={{ color: '#4b5563', lineHeight: '1.7', fontSize: '15px' }}>
                    {isLong && !showFullDescription ? <p style={{ margin: '0 0 16px 0' }}>{plain.substring(0, 200)}…</p> : <div dangerouslySetInnerHTML={{ __html: safe }} />}
                    {isLong && (
                      <button onClick={() => setShowFullDescription((v) => !v)} style={{ background: 'none', border: 'none', color: primaryColor, cursor: 'pointer', fontSize: '14px', fontWeight: '600', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                        {showFullDescription ? 'Read less' : 'Read more'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className={isEditionTheme ? 'edition-related-products' : isAtelierTheme ? 'atelier-related-products' : isGalleryTheme ? 'gallery-related-products' : ''} style={{ marginTop: '80px', paddingTop: '48px', borderTop: '1px solid #f3f4f6' }}>
            <div style={{ marginBottom: '32px' }}>
              {(isEditionTheme || isAtelierTheme || isGalleryTheme) && <p className={isAtelierTheme ? 'atelier-related-products__eyebrow' : isGalleryTheme ? 'gallery-related-products__eyebrow' : 'edition-related-products__eyebrow'}>{isAtelierTheme ? 'More to discover' : isGalleryTheme ? 'Further viewing' : 'Continue exploring'}</p>}
              <h2 className={isEditionTheme ? 'edition-related-products__title' : isAtelierTheme ? 'atelier-related-products__title' : isGalleryTheme ? 'gallery-related-products__title' : ''} style={{
                fontSize: '24px',
                fontWeight: '700',
                color: themeStyles?.text || '#111827',
                margin: '0 0 8px 0'
              }}>
                You might also like
              </h2>
              <p className={isEditionTheme ? 'edition-related-products__description' : isAtelierTheme ? 'atelier-related-products__description' : isGalleryTheme ? 'gallery-related-products__description' : ''} style={{
                color: themeStyles?.textMuted || '#6b7280',
                fontSize: '15px',
                margin: 0
              }}>
                Discover more products from our collection
              </p>
            </div>
            <div className="related-products-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '24px'
            }}>
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct._id || relatedProduct.id}
                  className={isEditionTheme ? 'edition-related-products__card' : isAtelierTheme ? 'atelier-related-products__card' : isGalleryTheme ? 'gallery-related-products__card' : ''}
                  onClick={() => navigate(getStorePath(`product/${relatedProduct.slug || relatedProduct._id || relatedProduct.id}`))}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: themeStyles?.background || '#ffffff',
                    border: '1px solid #f3f4f6',
                    transition: 'box-shadow 0.25s ease, transform 0.25s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    aspectRatio: '1',
                    backgroundColor: themeStyles?.backgroundAlt || '#f9fafb',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={getAllImageUrls(relatedProduct)[0] || getFallbackImageUrl()}
                      alt={relatedProduct.name}
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onError={(e) => { e.currentTarget.src = getFallbackImageUrl(); }}
                    />
                  </div>
                  <div style={{ padding: '16px' }}>
                    <h3 style={{
                      fontWeight: '600',
                      color: themeStyles?.text || '#111827',
                      fontSize: '15px',
                      margin: '0 0 6px 0',
                      lineHeight: '1.4'
                    }}>
                      {relatedProduct.name}
                    </h3>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: primaryColor,
                      margin: 0
                    }}>
                      {currency.format(relatedProduct.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky QR Code Flyout - Fixed to right side */}
      {checkoutQR && (
        <>
          {/* QR Toggle Button - Sticky on right side, hidden on mobile.
              Offset above center so it stacks with the WhatsApp flyout
              (which sits at top: 50%) instead of overlapping it. */}
          <button
            onClick={() => setShowQR(!showQR)}
            className="qr-flyout-toggle"
            style={{
              position: 'fixed',
              right: showQR ? '220px' : '0',
              top: 'calc(50% - 80px)',
              transform: 'translateY(-50%)',
              zIndex: 1000,
              padding: '12px 8px',
              backgroundColor: '#10b981',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px 0 0 8px',
              cursor: 'pointer',
              boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
              transition: 'right 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}
            title="Scan QR to checkout"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: 'rotate(90deg)' }}>
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3h-3zM17 17h4v4h-4zM14 17v4M17 14h4" />
            </svg>
            <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
              {showQR ? 'CLOSE' : 'QR'}
            </span>
          </button>

          {/* QR Flyout Panel — same vertical anchor as the toggle so they
              stay aligned. Sits above the WhatsApp tab at rest; when open,
              the panel naturally takes focus and any brief overlap with
              the WhatsApp tab is acceptable while the user is mid-scan. */}
          <div
            className="qr-flyout-panel"
            style={{
              position: 'fixed',
              right: showQR ? '0' : '-220px',
              top: 'calc(50% - 80px)',
              transform: 'translateY(-50%)',
              zIndex: 999,
              width: '200px',
              padding: '20px 16px',
              backgroundColor: themeStyles?.background || '#ffffff',
              borderRadius: '12px 0 0 12px',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
              transition: 'right 0.3s ease',
              textAlign: 'center'
            }}
          >
            <p style={{
              fontSize: '12px',
              fontWeight: '600',
              color: themeStyles?.text || '#374151',
              margin: '0 0 12px 0'
            }}>
              Scan to Checkout
            </p>
            <img
              src={checkoutQR.qrCode}
              alt="Checkout QR Code"
              style={{
                width: '140px',
                height: '140px',
                borderRadius: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                padding: '6px'
              }}
            />
            <p style={{
              fontSize: '10px',
              color: themeStyles?.textMuted || '#9ca3af',
              margin: '10px 0 0 0',
              lineHeight: 1.4
            }}>
              Point your camera at the QR code
            </p>
          </div>
        </>
      )}

      {/* Hide desktop QR side-flyout on mobile — the StorefrontBottomNav
          owns the bottom edge there, and a second fixed bar would cover it. */}
      <style>{`
        @media (max-width: 768px) {
          .qr-flyout-toggle,
          .qr-flyout-panel {
            display: none !important;
          }
        }
      `}</style>

      {/* WhatsApp Order Modal */}
      <WhatsAppOrderModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        product={{
          ...product,
          price: currentDisplayPrice || product?.price
        }}
        productQuantity={quantity}
        selectedVariations={selectedVariations}
        storeSettings={store}
        storeSlug={storeSlug}
      />
    </div>
    </div>
  );
};

export default ProductPage;
